<template>
  <div>
    <empty-state
      v-if="!restaurantIds"
      icon="mdi-silverware"
      :title="$t('select_restaurant')"
      :subtitle="$t('please_select_restaurant_to_view_data')"
    />

    <meals-table
      v-else
      :data="data"
      :loading="isLoading"
      :search-query="searchQuery"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MealsTable from "@/components/guest/MealsTable.vue";
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "AdminMealsTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    EmptyState,
    MealsTable,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
    selectedRestaurant: Object,
    selectedRestaurants: Array,
    restaurantIds: String,
  },
  data() {
    return {
      data: [],
      isLoading: false,
    };
  },
  computed: {
    restaurantNames() {
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        return this.selectedRestaurants.map(r => r.name).join(', ');
      }
      return 'Admin';
    },
    restaurantSummary() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      const total = this.selectedRestaurants.length;
      return `${this.$t("restaurants")} (${total})`;
    },
    restaurantNamesForFooter() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      return this.selectedRestaurants.map((r) => r.name).join(", ");
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("food"), value: "name", sortable: false },
        {
          text: this.$t("quantity"),
          value: "quantity",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("notes"),
          value: "prilog",
          sortable: false,
        },
      ];
    },
    totalQuantity() {
      return this.data.reduce((sum, item) => sum + item.quantity, 0);
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printMealsTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportMealsExcel();
    },
    dateRange: {
      handler() {
        if (this.restaurantIds) {
          this.loadData();
        }
      },
    },
    restaurantIds: {
      handler() {
        if (this.restaurantIds) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    async loadData() {
      if (!this.restaurantIds || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
          restaurants: this.restaurantIds,
        };

        const response = await reportService.getMultipleRestaurantGuest(params);
        this.processData(response.data);
      } catch (error) {
        console.error("Error loading meals data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    processData(data) {
      const foodsMap = {};

      Object.keys(data).forEach((shift) => {
        const shiftData = data[shift];

        Object.keys(shiftData).forEach((mealName) => {
          const parentMeal = shiftData[mealName];
          const parentQuantity = parentMeal.quantity;

          const prilogText =
            parentMeal.prilog && parentMeal.prilog.length > 0
              ? parentMeal.prilog.map((p) => p.name).join(", ")
              : "";

          if (parentMeal.foods && Array.isArray(parentMeal.foods)) {
            parentMeal.foods.forEach((food) => {
              const foodId = food.id;

              if (foodsMap[foodId]) {
                foodsMap[foodId].quantity += parentQuantity;
                if (prilogText && !foodsMap[foodId].prilog.includes(prilogText)) {
                  foodsMap[foodId].prilog = foodsMap[foodId].prilog
                    ? `${foodsMap[foodId].prilog}, ${prilogText}`
                    : prilogText;
                }
              } else {
                foodsMap[foodId] = {
                  id: food.id,
                  name: food.name,
                  quantity: parentQuantity,
                  prilog: prilogText,
                };
              }
            });
          }
        });
      });

      this.data = Object.values(foodsMap).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },
    printMealsTable() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: this.toLatin(item.name),
        quantity: item.quantity,
        prilog: item.prilog || '',
      }));

      tableItems.push({
        num: '',
        name: `<strong>${this.$t('total').toUpperCase()}</strong>`,
        quantity: `<strong>${this.totalQuantity}</strong>`,
        prilog: '',
      });

      this.printDocument({
        pageTitle: this.$t('routes.jela'),
        title: this.$t('routes.jela'),
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}<h4 style="text-align: center">${this.restaurantSummary}</h4>`,
        tables: [
          {
            headers: this.headers.map(h => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
          }
        ],
        afterTableHtml:
          this.selectedRestaurants && this.selectedRestaurants.length > 0
            ? `<div style="margin-top: 16px; font-size: 12px;"><strong>${this.$t(
                "restaurants"
              )}:</strong> ${this.restaurantNamesForFooter}</div>`
            : "",
      });
    },
    exportMealsExcel() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: this.toLatin(item.name),
        quantity: item.quantity,
        prilog: item.prilog || '',
      }));

      tableItems.push({
        num: '',
        name: this.$t('total').toUpperCase(),
        quantity: this.totalQuantity,
        prilog: '',
      });

      this.downloadExcel({
        fileName: `${this.restaurantNames.replace(/,/g, "_") || "admin"}_jela_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t('routes.jela'),
            title: this.$t('routes.jela'),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )} - ${this.restaurantSummary}`,
            headers: this.headers.map(h => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
            footer:
              this.selectedRestaurants && this.selectedRestaurants.length > 0
                ? `${this.$t("restaurants")}: ${this.restaurantNamesForFooter}`
                : "",
          }
        ],
      });
    },
  },
};
</script>
